import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Stack from '@mui/joy/Stack';
import CircularProgress from '@mui/joy/CircularProgress';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import { categoriesApi } from '../../api/categories.api';
import { attributeSetsApi } from '../../api/attribute-sets.api';
import { CreateCategoryDto } from '../../types/category';
import { AttributeSet } from '../../types/attribute-set';
import { formatRecordReference } from '../../utils/formatRecordReference';

const CategoryForm: React.FC = () => {
  const navigate = useNavigate();
  const { reference } = useParams<{ reference?: string }>();
  const categoryReference = reference as string | undefined;
  const [loading, setLoading] = useState(false);
  const [loadingAttributeSets, setLoadingAttributeSets] = useState(false);
  const [attributeSets, setAttributeSets] = useState<AttributeSet[]>([]);
  const [formData, setFormData] = useState<CreateCategoryDto>({
    name: '',
    description: '',
    attributeSetReference: undefined,
  });

  useEffect(() => {
    loadAttributeSets();
    if (categoryReference) {
      loadCategory(categoryReference);
    }
  }, [categoryReference]);

  const loadAttributeSets = async () => {
    try {
      setLoadingAttributeSets(true);
      const sets = await attributeSetsApi.getAll();
      setAttributeSets(sets);
    } catch (error) {
      console.error('Error al cargar attribute sets:', error);
    } finally {
      setLoadingAttributeSets(false);
    }
  };

  const loadCategory = async (ref: string) => {
    try {
      setLoading(true);
      const category = await categoriesApi.getOne(ref);
      setFormData({
        name: category.name,
        description: category.description || '',
        attributeSetReference: category.attributeSet?.reference ?? undefined,
      });
    } catch (error) {
      console.error('Error al cargar categoría:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (categoryReference) {
        await categoriesApi.update(categoryReference, formData);
      } else {
        await categoriesApi.create(formData);
      }
      navigate('/categories');
    } catch (error) {
      console.error('Error al guardar categoría:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  if (loading && categoryReference) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 640, mx: 'auto', width: '100%' }}>
      <Typography level="h2" sx={{ mb: 2 }}>
        {categoryReference
          ? `Editar categoría #${formatRecordReference(categoryReference)}`
          : 'Nueva categoría'}
      </Typography>
      <Sheet variant="outlined" sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Nombre</FormLabel>
              <Input name="name" value={formData.name} onChange={handleChange} />
            </FormControl>
            <FormControl>
              <FormLabel>Descripción</FormLabel>
              <Textarea name="description" value={formData.description} onChange={handleChange} minRows={3} />
            </FormControl>
            <FormControl>
              <FormLabel>Conjunto de Atributos</FormLabel>
              <Select
                value={formData.attributeSetReference || ''}
                onChange={(_, val) =>
                  setFormData({
                    ...formData,
                    attributeSetReference: val || undefined,
                  })
                }
                placeholder="Sin conjunto de atributos"
                disabled={loadingAttributeSets}
              >
                <Option value="">Sin conjunto de atributos</Option>
                {attributeSets.map((set) => (
                  <Option key={set.reference} value={set.reference}>
                    {set.name}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ pt: 1 }}>
              <Button type="submit" color="primary" loading={loading}>
                {categoryReference ? 'Actualizar' : 'Crear'}
              </Button>
              <Button type="button" variant="outlined" color="neutral" onClick={() => navigate('/categories')}>
                Cancelar
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Sheet>
    </Box>
  );
};

export default CategoryForm;
